/**
 * Outreach v2 — Zustand store.
 *
 * Holds accounts + campaigns + the org compliance settings used by the Settings
 * surface, with loading/error state per the ATS Zustand convention. Actions call
 * the API directly and keep local state in sync optimistically where it's safe.
 *
 * Org-scoping is handled server-side (every endpoint reads `req.organizationId`),
 * so the store never threads an org id around.
 *
 * NOTE: org-level compliance (postal address + unsubscribe footer) has no
 * backend endpoint in P1–P4 yet. Until the compliance API lands we persist those
 * two fields in localStorage as a stopgap so the Settings tab is fully usable;
 * per-account `signature` already persists via the real accounts endpoint.
 */

import { create } from 'zustand'
import { registerOrgScopedReset } from '@/store/orgScopedReset'
import { accountsApi, campaignsApi, suppressionApi } from '../services/outreach.api'
import type {
  OutreachAccount,
  OutreachCampaign,
  OutreachSuppression,
  ConnectAccountPayload,
  UpdateAccountPayload,
  AddSuppressionPayload,
  ListSuppressionsParams,
  ComplianceSettings,
} from '../types'

type AsyncStatus = 'idle' | 'loading' | 'loaded' | 'error'

const COMPLIANCE_STORAGE_KEY = 'outreach-v2:compliance-settings'

const DEFAULT_COMPLIANCE: ComplianceSettings = {
  orgPostalAddress: '',
  unsubscribeFooter:
    'You are receiving this because we believe this role may be relevant to you. Reply STOP to unsubscribe.',
}

function loadCompliance(): ComplianceSettings {
  if (typeof window === 'undefined') return DEFAULT_COMPLIANCE
  try {
    const raw = window.localStorage.getItem(COMPLIANCE_STORAGE_KEY)
    if (!raw) return DEFAULT_COMPLIANCE
    return { ...DEFAULT_COMPLIANCE, ...JSON.parse(raw) }
  } catch {
    return DEFAULT_COMPLIANCE
  }
}

function persistCompliance(settings: ComplianceSettings): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(COMPLIANCE_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* noop — private-mode quota etc. */
  }
}

function errMsg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string }
  return e?.response?.data?.error || e?.message || fallback
}

interface OutreachState {
  /* Accounts */
  accounts: OutreachAccount[]
  accountsStatus: AsyncStatus
  accountsError: string | null

  /* Campaigns */
  campaigns: OutreachCampaign[]
  campaignsStatus: AsyncStatus
  campaignsError: string | null

  /* Suppressions */
  suppressions: OutreachSuppression[]
  suppressionsStatus: AsyncStatus
  suppressionsError: string | null

  /* Org compliance (client-persisted stopgap) */
  compliance: ComplianceSettings

  /* Account actions */
  loadAccounts: () => Promise<void>
  connectAccount: (payload: ConnectAccountPayload) => Promise<string | null>
  updateAccount: (id: string, payload: UpdateAccountPayload) => Promise<void>
  refreshLinkedInFeatures: (id: string) => Promise<void>
  removeAccount: (id: string) => Promise<void>
  /** Reconnect in place (Stage 1). Returns a hosted-auth URL to open, or null. */
  reconnectAccount: (id: string) => Promise<string | null>
  /** Claim an on_hold account ("Disconnect from all other organizations").
   *  Returns a fresh hosted-auth URL — the claim ends with one clean re-sign-in
   *  (deleting any duplicate connection revokes the shared Google grant, so the
   *  old connections can never be kept). */
  claimAccount: (id: string) => Promise<string | null>

  /* Campaign actions */
  loadCampaigns: (params?: { jobId?: string }) => Promise<void>

  /* Suppression actions */
  loadSuppressions: (params?: ListSuppressionsParams) => Promise<void>
  addSuppression: (payload: AddSuppressionPayload) => Promise<void>
  removeSuppression: (id: string) => Promise<void>

  /* Compliance actions */
  loadCompliance: () => void
  updateCompliance: (patch: Partial<ComplianceSettings>) => void

  reset: () => void
}

const initialState = {
  accounts: [] as OutreachAccount[],
  accountsStatus: 'idle' as AsyncStatus,
  accountsError: null as string | null,
  campaigns: [] as OutreachCampaign[],
  campaignsStatus: 'idle' as AsyncStatus,
  campaignsError: null as string | null,
  suppressions: [] as OutreachSuppression[],
  suppressionsStatus: 'idle' as AsyncStatus,
  suppressionsError: null as string | null,
  compliance: DEFAULT_COMPLIANCE,
}

export const useOutreachStore = create<OutreachState>((set, get) => ({
  ...initialState,

  /* ─── Accounts ─── */
  loadAccounts: async () => {
    set({ accountsStatus: 'loading', accountsError: null })
    try {
      const { data } = await accountsApi.list()
      set({ accounts: data.accounts || [], accountsStatus: 'loaded' })
    } catch (err) {
      set({
        accountsStatus: 'error',
        accountsError: errMsg(err, 'Failed to load accounts'),
      })
    }
  },

  connectAccount: async (payload) => {
    set({ accountsError: null })
    try {
      const { data } = await accountsApi.connect(payload)
      // Show the new account right away. On the real hosted-auth path it comes
      // back `pending` — stash the hosted-auth URL on the row so the card can
      // offer "Finish connecting" until the accounts webhook flips it connected.
      const account = data.hostedAuthUrl
        ? { ...data.account, hostedAuthUrl: data.hostedAuthUrl }
        : data.account
      set((s) => {
        // Re-adding an account you already have (alreadyConnected) or reconnecting
        // in place returns an EXISTING row — merge it rather than prepend a twin.
        const exists = s.accounts.some((a) => a._id === account._id)
        return {
          accounts: exists
            ? s.accounts.map((a) => (a._id === account._id ? { ...a, ...account } : a))
            : [account, ...s.accounts],
        }
      })
      // Hosted-auth URL present → caller opens it to finish the connection.
      return data.hostedAuthUrl
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to connect account') })
      throw err
    }
  },

  updateAccount: async (id, payload) => {
    set({ accountsError: null })
    try {
      const { data } = await accountsApi.update(id, payload)
      set((s) => ({
        accounts: s.accounts.map((a) => (a._id === id ? { ...a, ...data.account } : a)),
      }))
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to update account') })
      throw err
    }
  },

  refreshLinkedInFeatures: async (id) => {
    set({ accountsError: null })
    try {
      const { data } = await accountsApi.refreshLinkedInFeatures(id)
      set((s) => ({
        accounts: s.accounts.map((a) => (a._id === id ? { ...a, ...data.account } : a)),
      }))
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to refresh LinkedIn features') })
      throw err
    }
  },

  removeAccount: async (id) => {
    set({ accountsError: null })
    try {
      await accountsApi.remove(id)
      set((s) => ({ accounts: s.accounts.filter((a) => a._id !== id) }))
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to remove account') })
      throw err
    }
  },

  reconnectAccount: async (id) => {
    set({ accountsError: null })
    try {
      const { data } = await accountsApi.reconnect(id)
      // Reflect the revived row (now `pending`, or bound in simulated mode) and
      // stash the hosted-auth URL so the card can offer "Finish connecting".
      const account = data.hostedAuthUrl ? { ...data.account, hostedAuthUrl: data.hostedAuthUrl } : data.account
      set((s) => ({ accounts: s.accounts.map((a) => (a._id === id ? { ...a, ...account } : a)) }))
      return data.hostedAuthUrl
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to reconnect account') })
      throw err
    }
  },

  claimAccount: async (id) => {
    set({ accountsError: null })
    try {
      const { data } = await accountsApi.claim(id)
      // A claim purges every Unipile connection for this identity (deleting any
      // one revokes the shared Google OAuth grant — all would die anyway) and
      // returns a FRESH hosted-auth URL: the row comes back `pending` and the
      // caller opens the URL for one final Google sign-in.
      const account = data.hostedAuthUrl ? { ...data.account, hostedAuthUrl: data.hostedAuthUrl } : data.account
      set((s) => ({ accounts: s.accounts.map((a) => (a._id === id ? { ...a, ...account } : a)) }))
      return data.hostedAuthUrl || null
    } catch (err) {
      set({ accountsError: errMsg(err, 'Failed to claim account') })
      throw err
    }
  },

  /* ─── Campaigns ─── */
  loadCampaigns: async (params) => {
    set({ campaignsStatus: 'loading', campaignsError: null })
    try {
      const { data } = await campaignsApi.list(params)
      set({ campaigns: data.campaigns || [], campaignsStatus: 'loaded' })
    } catch (err) {
      set({
        campaignsStatus: 'error',
        campaignsError: errMsg(err, 'Failed to load campaigns'),
      })
    }
  },

  /* ─── Suppressions ─── */
  loadSuppressions: async (params) => {
    set({ suppressionsStatus: 'loading', suppressionsError: null })
    try {
      const { data } = await suppressionApi.list(params)
      set({ suppressions: data.suppressions || [], suppressionsStatus: 'loaded' })
    } catch (err) {
      set({
        suppressionsStatus: 'error',
        suppressionsError: errMsg(err, 'Failed to load suppression list'),
      })
    }
  },

  addSuppression: async (payload) => {
    set({ suppressionsError: null })
    try {
      const { data } = await suppressionApi.add(payload)
      set((s) => ({ suppressions: [data.suppression, ...s.suppressions] }))
    } catch (err) {
      set({ suppressionsError: errMsg(err, 'Failed to add suppression') })
      throw err
    }
  },

  removeSuppression: async (id) => {
    set({ suppressionsError: null })
    try {
      await suppressionApi.remove(id)
      set((s) => ({ suppressions: s.suppressions.filter((x) => x._id !== id) }))
    } catch (err) {
      set({ suppressionsError: errMsg(err, 'Failed to remove suppression') })
      throw err
    }
  },

  /* ─── Compliance (client-persisted stopgap) ─── */
  loadCompliance: () => set({ compliance: loadCompliance() }),

  updateCompliance: (patch) => {
    const next = { ...get().compliance, ...patch }
    persistCompliance(next)
    set({ compliance: next })
  },

  reset: () => set(initialState),
}))

// Org switch (no page reload): drop this org's cached accounts/campaigns/
// suppressions so the next mount refetches for the NEW organization instead of
// rendering the previous org's rows (statuses return to 'idle').
registerOrgScopedReset(() => useOutreachStore.getState().reset())
